import {
  enqueueWrite,
  listPendingWrites,
  listAllWrites,
  updateWriteStatus,
  recordSyncFailure,
  getSyncFailureHistory,
  WriteQueueRow,
  SyncFailureRow,
} from './db';

export function newBatchId(): string {
  return crypto.randomUUID();
}

export interface QueuedWrite {
  entityType: string;
  handler: string;
  payload: unknown;
  /** client_uuid values of writes in the same batch this one depends on */
  dependsOn?: string[];
  patientUuid?: string | null;
  clientUuid?: string;
}

/**
 * Enqueues a dependency-ordered batch of create-only writes (e.g. one
 * consultation submission: encounter, then its conditions/allergies/etc.)
 * under a single batch_id, so the sync engine can replay them together
 * and in order. Each item is inserted sequentially so earlier items'
 * client_uuid values are available for later items' dependsOn.
 */
export async function enqueueBatch(
  writes: QueuedWrite[],
  batchId: string = newBatchId()
): Promise<WriteQueueRow[]> {
  const rows: WriteQueueRow[] = [];
  for (const write of writes) {
    const row = await enqueueWrite({
      entityType: write.entityType,
      handler: write.handler,
      payload: write.payload,
      dependsOn: write.dependsOn ?? [],
      batchId,
      patientUuid: write.patientUuid ?? null,
      clientUuid: write.clientUuid,
    });
    rows.push(row);
  }
  return rows;
}

export function getPendingWrites(): Promise<WriteQueueRow[]> {
  return listPendingWrites();
}

export function getAllWrites(): Promise<WriteQueueRow[]> {
  return listAllWrites();
}

export function markWriteStatus(
  clientUuid: string,
  status: WriteQueueRow['status'],
  lastError?: string | null,
  nextRetryAt?: string | null
): Promise<void> {
  return updateWriteStatus({ clientUuid, status, lastError, nextRetryAt });
}

/** Appends one entry to the write's failure history (see sync_failures). */
export function logSyncFailure(
  row: Pick<WriteQueueRow, 'client_uuid' | 'entity_type' | 'handler' | 'attempt_count'>,
  error: string
): Promise<void> {
  return recordSyncFailure({
    clientUuid: row.client_uuid,
    entityType: row.entity_type,
    handler: row.handler,
    error,
    attemptNumber: row.attempt_count,
  });
}

export function getFailureHistory(clientUuid: string): Promise<SyncFailureRow[]> {
  return getSyncFailureHistory(clientUuid);
}
